import {
  DICTATION_AUDIO_MAX_SAMPLES,
  DICTATION_AUDIO_SAMPLE_RATE,
  validateDictationAudio,
} from '@oncatch/types/dictation-audio';

const SOURCE_MAX_BYTES = 4 * 1024 * 1024;
const ENCODE_TIMEOUT_MS = 10_000;
function ensureActive(signal: AbortSignal): void {
  if (signal.aborted) throw new Error('dictation_encoding_cancelled');
}
function abortable<T>(operation: Promise<T>, signal: AbortSignal): Promise<T> {
  return new Promise((resolve, reject) => {
    const abort = () => {
      signal.removeEventListener('abort', abort);
      reject(new Error('dictation_encoding_cancelled'));
    };
    signal.addEventListener('abort', abort, { once: true });
    if (signal.aborted) abort();
    operation.then(
      (value) => {
        signal.removeEventListener('abort', abort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener('abort', abort);
        reject(error);
      },
    );
  });
}
function audioConstructor(): typeof AudioContext | undefined {
  return (
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  );
}
export function canEncodeDictationAudio(): boolean {
  const Audio = audioConstructor();
  return (
    typeof Audio === 'function' &&
    typeof Audio.prototype?.decodeAudioData === 'function' &&
    typeof Audio.prototype?.close === 'function'
  );
}
/** Decode locally; only fixed-format PCM samples may leave the browser for Speak. */
export async function encodeDictationAudio(source: Blob, signal: AbortSignal): Promise<Blob> {
  if (source.size === 0 || source.size > SOURCE_MAX_BYTES || signal.aborted)
    throw new Error('dictation_audio_invalid');
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal.addEventListener('abort', abort, { once: true });
  const timer = setTimeout(abort, ENCODE_TIMEOUT_MS);
  let context: AudioContext | undefined;
  let result: Blob | undefined;
  try {
    const bytes = await abortable(source.arrayBuffer(), controller.signal);
    ensureActive(controller.signal);
    const Audio = audioConstructor();
    if (!Audio) throw new Error('dictation_audio_unsupported');
    context = new Audio({ sampleRate: DICTATION_AUDIO_SAMPLE_RATE });
    if (context.sampleRate !== DICTATION_AUDIO_SAMPLE_RATE)
      throw new Error('dictation_audio_unsupported');
    const audio = await abortable(context.decodeAudioData(bytes), controller.signal);
    ensureActive(controller.signal);
    if (
      audio.sampleRate !== DICTATION_AUDIO_SAMPLE_RATE ||
      !Number.isInteger(audio.length) ||
      audio.length < 1 ||
      audio.length > DICTATION_AUDIO_MAX_SAMPLES ||
      !Number.isFinite(audio.duration) ||
      audio.duration <= 0 ||
      audio.duration > 60 ||
      ![1, 2].includes(audio.numberOfChannels)
    )
      throw new Error('dictation_audio_invalid');
    const channels = Array.from({ length: audio.numberOfChannels }, (_, index) =>
      audio.getChannelData(index),
    );
    if (channels.some((channel) => channel.length !== audio.length))
      throw new Error('dictation_audio_invalid');
    const output = new Uint8Array(44 + audio.length * 2);
    const view = new DataView(output.buffer);
    const tag = (offset: number, text: string) => {
      for (let i = 0; i < text.length; i++) output[offset + i] = text.charCodeAt(i);
    };
    tag(0, 'RIFF');
    view.setUint32(4, output.length - 8, true);
    tag(8, 'WAVE');
    tag(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, DICTATION_AUDIO_SAMPLE_RATE, true);
    view.setUint32(28, DICTATION_AUDIO_SAMPLE_RATE * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    tag(36, 'data');
    view.setUint32(40, audio.length * 2, true);
    for (let i = 0; i < audio.length; i++) {
      let sample = 0;
      for (const channel of channels) {
        const value = channel[i];
        if (value === undefined || !Number.isFinite(value))
          throw new Error('dictation_audio_invalid');
        sample += value / channels.length;
      }
      sample = Math.max(-1, Math.min(1, sample));
      view.setInt16(44 + i * 2, Math.round(sample * (sample < 0 ? 32768 : 32767)), true);
      if (i % 16_384 === 0) {
        await abortable(new Promise<void>((resolve) => setTimeout(resolve, 0)), controller.signal);
        ensureActive(controller.signal);
      }
    }
    if (!validateDictationAudio(output)) throw new Error('dictation_audio_invalid');
    ensureActive(controller.signal);
    result = new Blob([output], { type: 'audio/wav' });
  } finally {
    clearTimeout(timer);
    signal.removeEventListener('abort', abort);
    if (context) {
      // close() may not settle promptly on a broken decoder. Never await it forever.
      let closeTimer: ReturnType<typeof setTimeout> | undefined;
      try {
        await Promise.race([
          context.close(),
          new Promise<never>((_, reject) => {
            closeTimer = setTimeout(
              () => reject(new Error('dictation_audio_cleanup_failed')),
              1_000,
            );
          }),
        ]);
      } finally {
        clearTimeout(closeTimer);
      }
    }
  }
  if (signal.aborted || controller.signal.aborted) throw new Error('dictation_encoding_cancelled');
  if (!result) throw new Error('dictation_audio_invalid');
  return result;
}
